import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react';
import {
    Button,
    Dropdown,
    DropdownButton,
    DropdownItem,
    DropdownList,
    DropdownTrigger,
    DropdownTriggerContext,
    SubNavbar,
    StickyHeader,
    useMediaQuery,
} from '@bi-digital/react';
import { ChevronDownIcon, DotLargeIcon } from '@bi-digital/icons';
import { useTranslation } from 'react-i18next';

import styles from './ProgrammeNav.module.css';
import type { ColorScheme, Colors } from '@bi-digital/react/react-types';

const getSectionName = (element?: HTMLDivElement) => element?.getAttribute('data-menu-label') || '';

const useScrollTheme = <T extends HTMLElement = HTMLElement>() => {
    const ref = useRef<T>(null);
    const [theme, setTheme] = useState<{ colorScheme: ColorScheme; color: Colors }>({
        colorScheme: 'light',
        color: 'neutral',
    });

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        const update = () => {
            const rect = element.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;

            const elementsBehind = document.elementsFromPoint(centerX, centerY) as HTMLElement[];
            const themeSection = elementsBehind
                .find((el) => el.closest('div[data-color-scheme][data-color]'))
                ?.closest<HTMLElement>('div[data-color-scheme][data-color]');

            setTheme({
                colorScheme: (themeSection?.dataset.colorScheme as ColorScheme) ?? 'light',
                color: (themeSection?.dataset.color as Colors) ?? 'neutral',
            });
        };

        update();
        window.addEventListener('scroll', update, { passive: true });
        return () => window.removeEventListener('scroll', update);
    }, []);

    return { ref, theme };
};

const ThemedParagraph = (props: React.HTMLAttributes<HTMLParagraphElement>) => {
    const { ref, theme } = useScrollTheme<HTMLParagraphElement>();
    return <p {...props} ref={ref} data-color-scheme={theme.colorScheme} data-color={theme.color} />;
};

type SideMenuNavItemProps = {
    element: HTMLDivElement;
    activeId?: string;
};

const SideMenuNavItem = ({ element, activeId }: SideMenuNavItemProps) => {
    const { ref, theme } = useScrollTheme<HTMLLIElement>();
    return (
        <li ref={ref} data-color-scheme={theme.colorScheme} data-color={theme.color}>
            <Button asChild className={styles.button} variant='text' aria-current={activeId === element.id}>
                <a href={`#${element.id}`}>{getSectionName(element)}</a>
            </Button>
        </li>
    );
};

export type ProgrammeNavProps = {
    name: string;
    label: string | null;
    credits: number | null;
    action: ReactNode;
};

export const ProgrammeNav = ({ name, label, credits, action }: ProgrammeNavProps) => {
    const { t } = useTranslation();
    const isLargeScreen = useMediaQuery('(width >= 1024px)');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [activeDropdownLink, setActiveDropdownLink] = useState<HTMLDivElement | undefined>(undefined);
    const dropdownLinks = [
        ...document.querySelectorAll<HTMLDivElement>(`[data-section="true"][data-menu-label][id]`),
    ].filter((element) => getSectionName(element) && element.id);

    useEffect(() => {
        const updateActiveIndicator = () => {
            const activeDropdownLink = dropdownLinks.findLast((element) => element.getBoundingClientRect().top < 84);
            setActiveDropdownLink(activeDropdownLink || dropdownLinks[0]);
        };

        updateActiveIndicator();

        window.addEventListener('scroll', updateActiveIndicator, { passive: true });
        return () => {
            window.removeEventListener('scroll', updateActiveIndicator);
        };
    }, [dropdownLinks]);

    return (
        <>
            {isLargeScreen ? (
                <StickyHeader>
                    <aside className={styles.sidemenu}>
                        <ul className={styles.buttonList}>
                            {dropdownLinks.map((element) => (
                                <SideMenuNavItem key={element.id} element={element} activeId={activeDropdownLink?.id} />
                            ))}
                        </ul>
                        <div>
                            {label && (
                                <ThemedParagraph className={styles.programmeInfo}>
                                    {label}
                                    {!!credits && (
                                        <>
                                            <DotLargeIcon aria-hidden='true' />
                                            <span>{t('cart:credits', { count: credits })}</span>
                                        </>
                                    )}
                                </ThemedParagraph>
                            )}
                            <ThemedParagraph className={styles.programmeName}>{name}</ThemedParagraph>
                            {action}
                        </div>
                    </aside>
                </StickyHeader>
            ) : (
                <SubNavbar className={styles.programmePageNavigation} data-color-scheme='dark'>
                    <div>
                        <DropdownTriggerContext open={dropdownOpen} onOpenChange={setDropdownOpen}>
                            <DropdownTrigger
                                className={styles.trigger}
                                endIcon={<ChevronDownIcon aria-hidden='true' className={styles.triggerIcon} />}
                            >
                                <span>
                                    <span>{name}</span>
                                    <span>{getSectionName(activeDropdownLink) || ''}</span>
                                </span>
                            </DropdownTrigger>
                            <Dropdown
                                placement='bottom-start'
                                padding={0}
                                className={styles.dropdown}
                                data-color-scheme='light'
                                data-color='primary'
                            >
                                <DropdownList className={styles.buttonList}>
                                    {dropdownLinks.map((element, index) => (
                                        <Fragment key={element.id}>
                                            {index !== 0 && (
                                                <DropdownItem aria-hidden='true'>
                                                    <hr />
                                                </DropdownItem>
                                            )}
                                            <DropdownItem>
                                                <DropdownButton
                                                    asChild
                                                    className={styles.button}
                                                    onClick={() => setDropdownOpen(false)}
                                                    aria-current={activeDropdownLink?.id === element.id}
                                                >
                                                    <a href={`#${element.id}`}>{getSectionName(element)}</a>
                                                </DropdownButton>
                                            </DropdownItem>
                                        </Fragment>
                                    ))}
                                </DropdownList>
                            </Dropdown>
                        </DropdownTriggerContext>
                        {action}
                    </div>
                </SubNavbar>
            )}
        </>
    );
};
